import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  SixQuestionForm,
  type FormValues,
  type Credentials,
} from '@/components/onboarding';
import { useUserProfile, usePatchUserProfile } from '@/hooks/use-user-profile';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { posthog } from '../lib/posthog';

/**
 * Install an implicit-flow session from the URL hash when present.
 *
 * Supabase's `auth.admin.inviteUserByEmail` produces an implicit-flow
 * verify endpoint — when the recipient clicks the magic link, GoTrue
 * redirects with `#access_token=...&refresh_token=...` in the URL hash.
 * The Arcanum web client is configured with `flowType: 'pkce'`, so
 * `detectSessionInUrl` only auto-parses `?code=` query params; the
 * implicit-flow hash is ignored and no session is installed.
 *
 * This helper extracts the hash params and explicitly installs the
 * session via `setSession()`. Idempotent — early-returns when the hash
 * is absent. Best-effort — logs and falls through on `setSession()`
 * failure so the page renders its expired/unauth fallback.
 */
async function installImplicitFlowSessionFromHash(): Promise<boolean> {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token=')) return false;
  const params = new URLSearchParams(
    hash.startsWith('#') ? hash.slice(1) : hash
  );
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return false;
  try {
    const { error } = await supabase.auth.setSession({
      access_token,
      refresh_token,
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[signup] setSession failed', error.message);
      return false;
    }
    // Strip the hash so refreshes don't re-attempt the install and so
    // downstream routing isn't confused by a stale token in the URL.
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[signup] setSession threw', err);
    return false;
  }
}

/**
 * Fire the admin-notification email for a newly-signed-up user.
 * Fire-and-forget — never blocks the signup UX. Errors are swallowed
 * silently because the server-side service is idempotent (next attempt
 * by the same web session or admin would re-fire) AND best-effort by
 * design (email failure ≠ signup failure). The POST is unauthenticated
 * by route; idempotency lives at the DB column
 * `user_profiles.signup_notification_sent_at`.
 */
function fireSignupNotification(userId: string): void {
  void api.post('/api/users/signup-notification', { userId }).catch(() => {
    // Swallow — server-side service logs failures.
  });
}

/**
 * /signup — single-flow wizard (signup-and-billing Phase 1, refined
 * 2026-05-05; email-confirm-roundtrip fix 2026-05-14).
 *
 * The wizard ALWAYS renders. Two modes:
 *
 *   - **Self-signup (unauth)**: 7 steps total — 6 profile questions + a
 *     final "Create your account" step that collects firstName/lastName/
 *     email/password + ToS acceptance. On FINISH the wizard fires
 *     Supabase signUp passing the 6 profile fields + terms_accepted_at
 *     in `options.data` (user_metadata). The `handle_new_user` DB trigger
 *     reads those from `auth.users.raw_user_meta_data` and writes them
 *     into `user_profiles` (incl. `onboarding_completed=true`) atomically
 *     with the auth.users INSERT — no session required.
 *
 *   - **Existing-user-backfill (auth, mid-onboarding)**: 6 steps — only the
 *     profile questions. Credentials already exist; the wizard's onComplete
 *     PATCHes the profile via `usePatchUserProfile`. Used by admin-invited
 *     users (where `inviteUserByEmail` doesn't carry wizard answers).
 *
 * Email confirmation:
 *
 *   - In **dev local** (Supabase auto-confirms): the user has a session
 *     immediately after signUp. The trigger has already written the
 *     profile, so we just navigate to `/cockpit` — the existing `__root.tsx`
 *     redirect cascade routes them appropriately (admin → `/cockpit`,
 *     pending → `<PendingApprovalPage>` in place).
 *   - In **prod** (email-confirmation required): signUp returns
 *     success-without-session. We surface the "Confirm your email"
 *     interstitial. The user clicks the email link, lands back at /signup
 *     authenticated. `__root.tsx`'s cascade reads `user_profiles` (already
 *     populated by the trigger) sees `onboarding_completed=true`, and
 *     does NOT route them back through the wizard. They land on
 *     `/pending-approval` (or `/cockpit` if pre-approved). The wizard
 *     answers from the prior visit are preserved because they were
 *     written at signUp time, not after session lands.
 */
/**
 * Open-redirect guard mirroring `login.tsx:safeNextPath` — only honor a
 * same-origin absolute path (`/foo`). Rejects protocol-relative + absolute
 * URLs. Returns the safe path or `null` (caller falls back to `/cockpit`).
 */
function safeNextPath(next: string | undefined): string | null {
  if (!next) return null;
  if (!next.startsWith('/')) return null;
  if (next.startsWith('//')) return null;
  return next;
}

/**
 * Post-signup share-link bridge (2026-06-04 fix). When the signup carries a
 * `?share=<uuid>` (the public deal-link "Unlock" CTA →
 * `authUrlWithShare('/signup', shareId)` → `?next=/deal/<id>&share=<id>`), the
 * post-signup redirect MUST route through `/auth/callback?share=<id>` so the
 * server-side `linkSharesForUser` hook fires — it lives ONLY on that bridge
 * route. Pre-fix, every signup-completion path (`window.location.assign`/SPA
 * `navigate`) went straight to `next` (`/deal/<id>`) and skipped the bridge, so
 * a prospect who signed up from a public deal link — even with a matching
 * PENDING share — never got the deal linked (couldn't fork; landed as an
 * unlinked viewer). The bridge links the share then redirects to the deal/inbox
 * itself. Returns null when there's no `share` (caller keeps its existing
 * `next`/`/cockpit` redirect). UUID-guarded so a malformed param degrades to
 * the normal redirect rather than a dead `/auth/callback`.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function shareLinkBridgeHref(share: string | undefined): string | null {
  if (!share || !UUID_RE.test(share)) return null;
  return `/auth/callback?share=${encodeURIComponent(share)}`;
}

export function SignupPage() {
  const { user, signUp, signOut, verifyEmailSignupOtp } = useAuth();
  const { needsOnboarding, isLoading: profileLoading } = useUserProfile();
  const patchProfile = usePatchUserProfile();
  const navigate = useNavigate();
  // Post-auth return path from the public deal-link Unlock CTA
  // (`authUrlWithShare('/signup', shareId)` → `?next=%2Fdeal%2F<id>&share=<id>`).
  // Honored on both the self-signup auto-confirm path and the invited-backfill
  // path so a prospect lands back on the deal. The prod email-confirm path
  // routes through `/auth/callback?type=signup`, which has its own `?share=`
  // handling — `next` only applies to the in-session navigates below.
  const search = useSearch({ strict: false }) as {
    next?: string;
    share?: string;
  };
  const nextTarget = safeNextPath(search.next);
  // When present, post-signup redirects route through the share-link bridge so
  // `linkSharesForUser` fires (see `shareLinkBridgeHref`). Null otherwise.
  const shareBridgeHref = shareLinkBridgeHref(search.share);

  // Phase 3 § 3.3 — fire signup_started ONCE on mount.
  // StrictMode ref-dedupe: React 18 dev double-invokes mount effects;
  // the ref early-return matches the <SentryPostHogIdentity> lastKeyRef precedent.
  const signupStartedFiredRef = useRef(false);
  useEffect(() => {
    if (signupStartedFiredRef.current) return;
    signupStartedFiredRef.current = true;
    posthog.capture('signup_started', {});
  }, []);

  // Invited-user implicit-flow hash install. GoTrue's invite redirect
  // produces `#access_token=...` (implicit flow); the PKCE-configured
  // client doesn't auto-process the hash. Install it explicitly here so
  // `useAuth().user` populates and the invited-branch render fires below.
  // StrictMode ref-dedupe matches the signupStarted pattern above.
  const hashInstallFiredRef = useRef(false);
  useEffect(() => {
    if (hashInstallFiredRef.current) return;
    hashInstallFiredRef.current = true;
    void installImplicitFlowSessionFromHash();
  }, []);

  // Prod email-confirmation interstitial state — set when signUp succeeds
  // but the user isn't immediately authenticated (Supabase prod default).
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupLoading, setSignupLoading] = useState(false);
  // Email-confirmation CODE (replaces the old click-the-link confirmation —
  // corporate scanners burn confirmation links). Entered on the interstitial
  // below; verified via `verifyEmailSignupOtp` (verifyOtp type:'signup').
  const [confirmCode, setConfirmCode] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingEmail) return;
    setConfirmError(null);
    setConfirmLoading(true);
    const { error: err } = await verifyEmailSignupOtp(
      pendingEmail,
      confirmCode.trim()
    );
    setConfirmLoading(false);
    if (err) {
      setConfirmError(err.message);
      return;
    }
    // Email confirmed + full session established. Full-page nav (NOT SPA
    // `navigate`) so the app re-bootstraps with the just-persisted session and
    // `AuthProvider` populates `user` BEFORE `__root.tsx`'s cascade runs — an
    // SPA navigate races the async `onAuthStateChange` and the cascade can fire
    // with `user` still null → flash `/login` (caught in the 2026-05-29 auth
    // walkthrough, S1). The trigger already wrote the profile
    // (onboarding_completed=true) at signUp, so the cascade routes to
    // /pending-approval (or /cockpit if pre-approved) — not back to the wizard.
    // Route through the share-link bridge when a `?share=` is present so
    // `linkSharesForUser` fires; otherwise the existing `next`/`/cockpit` path.
    window.location.assign(shareBridgeHref ?? nextTarget ?? '/cockpit');
  };

  // While the auth state probe is in flight, render nothing — avoids
  // briefly flashing the unauth wizard for a user who's actually signed in.
  // The eventual __root.tsx redirect handles already-onboarded users.
  if (profileLoading && user) {
    return null;
  }

  // Existing-user-backfill: signed in + onboarding incomplete.
  // 6-step wizard, no credentials.
  if (user && needsOnboarding) {
    const wizardError = patchProfile.error?.message ?? null;
    return (
      <>
        {/* Phase 4: invited-user (or self-signup with a stale session) email
           confirmation banner. Read-only — changing email requires sign-out +
           re-sign-up because the user is already authenticated for THIS email.
           research/phase-4.md § 8 #7 + § 11 risk #7 + Divergence #4. */}
        {user.email ? (
          <div className="mx-auto mb-4 max-w-2xl px-6 text-text-muted text-xs font-mono">
            Continuing as <span className="text-text">{user.email}</span>
          </div>
        ) : null}
        <SixQuestionForm
          // Invited users land here with a magic-link session but NO password
          // on `auth.users` (Supabase `inviteUserByEmail` doesn't set one).
          // The backfill wizard collects password + confirm + ToS as its 7th
          // step so the user can sign in via /login post-session-expiry.
          // First name / last name / email come from the invite's user_metadata
          // and are NOT re-collected.
          collectCredentials="password-only"
          loading={signupLoading || patchProfile.isPending}
          error={signupError ?? wizardError}
          onComplete={async (
            values: FormValues,
            credentials: Credentials | null,
            termsAcceptedAt: string
          ) => {
            setSignupError(null);
            setSignupLoading(true);
            try {
              // Set the password the magic-link session never collected.
              // `supabase.auth.updateUser({password})` runs against the
              // authenticated session (the magic-link one), so this works
              // before the session expires. On success the user can sign
              // in via /login with this password thereafter.
              if (credentials?.password) {
                const { error: pwErr } = await supabase.auth.updateUser({
                  password: credentials.password,
                });
                if (pwErr) {
                  setSignupError(pwErr.message);
                  return;
                }
              }
              await patchProfile.mutateAsync({
                firm_name: values.firm_name,
                // PATCH wire shape uses `work_role` (renamed 2026-05-25 from
                // `role` to eliminate field-name collision with JWT admin claim).
                work_role: values.work_role,
                asset_classes: values.asset_classes,
                markets: values.markets,
                focus: values.focus,
                volume: values.volume,
                onboarding_completed: true,
                terms_accepted_at: termsAcceptedAt,
              });
              // Phase 3 § 3.3 — account creation complete (backfill path).
              posthog.capture('signup_completed', {});
              if (shareBridgeHref) {
                // Route through the share-link bridge so linkSharesForUser fires.
                window.location.assign(shareBridgeHref);
              } else {
                navigate(
                  nextTarget
                    ? ({ to: nextTarget } as { to: string })
                    : { to: '/cockpit' }
                );
              }
            } finally {
              setSignupLoading(false);
            }
          }}
          onLoginClick={async () => {
            // Mid-onboarding user wants to switch accounts — sign out so the
            // re-entry through /login picks up cleanly.
            await signOut();
            navigate({ to: '/login' });
          }}
        />
      </>
    );
  }

  // Email-confirmation interstitial — only in prod when Supabase requires
  // the user to click the link in their inbox before getting a session.
  if (pendingEmail) {
    return (
      <div className="relative flex items-center justify-center min-h-screen bg-void">
        <div className="w-full max-w-sm px-8 py-10 flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-accent-dim border border-accent/20 flex items-center justify-center">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="text-center">
            <p className="text-base text-text-bright font-body font-medium mb-2">
              Confirm your email
            </p>
            <p className="text-sm text-text-muted font-mono leading-relaxed">
              We sent a 6-digit code to
              <br />
              <span className="text-text-bright">{pendingEmail}</span>
            </p>
          </div>

          <form
            onSubmit={handleConfirmCode}
            className="flex flex-col items-center gap-4 w-full"
          >
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Confirmation code"
              value={confirmCode}
              onChange={(e) =>
                setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              required
              autoFocus
              className="w-full px-4 py-3.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-xl text-center tracking-[0.5em] outline-none focus:border-accent/30 transition-colors"
            />

            {confirmError && (
              <div className="w-full px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px]">
                <p className="text-danger font-mono text-sm">{confirmError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={confirmLoading || confirmCode.length < 6}
              className="w-full py-3.5 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
            >
              {confirmLoading ? 'Confirming...' : 'Confirm & Continue'}
            </button>
          </form>

          <button
            onClick={() => navigate({ to: '/login' })}
            className="mt-1 text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted transition-colors"
          >
            &larr; Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Default: unauth self-signup. 7-step wizard with credentials at the end.
  return (
    <SixQuestionForm
      collectCredentials="full"
      loading={signupLoading || patchProfile.isPending}
      error={signupError}
      onComplete={async (
        values: FormValues,
        credentials: Credentials | null,
        termsAcceptedAt: string
      ) => {
        if (!credentials) {
          // Defensive — shouldn't happen when collectCredentials is true.
          setSignupError('Missing credentials');
          return;
        }
        setSignupError(null);
        setSignupLoading(true);
        // Pass profile + ToS in user_metadata so the handle_new_user DB
        // trigger writes them into user_profiles atomically with the
        // auth.users INSERT. Closes the email-confirm round-trip gap
        // where the prior PATCH-after-session path lost wizard answers
        // when the session never landed pre-confirmation. (2026-05-14)
        const {
          error: signUpErr,
          session,
          userId: newUserId,
        } = await signUp(credentials.email, credentials.password, {
          firstName: credentials.firstName,
          lastName: credentials.lastName,
          profile: {
            firm_name: values.firm_name,
            // user_metadata key stays `role` — the handle_new_user trigger
            // reads `raw_user_meta_data->>'role'` and writes to the renamed
            // `work_role` column (with admin-shaped sanitization). Form field
            // renamed to `work_role` 2026-05-25; mapped here at the boundary
            // for back-compat with in-flight signup sessions during deploy.
            role: values.work_role,
            asset_classes: values.asset_classes,
            markets: values.markets,
            focus: values.focus,
            volume: values.volume,
            terms_accepted_at: termsAcceptedAt,
          },
        });
        if (signUpErr) {
          setSignupLoading(false);
          setSignupError(signUpErr.message);
          return;
        }
        // Phase 3 § 3.3 — account creation complete (self-signup path).
        // Fires after signUp succeeds, before session/email-confirm branch —
        // the account exists at this point on BOTH paths (auto-confirm + prod email).
        posthog.capture('signup_completed', {});
        // Fire admin notification email. Fires on BOTH email-confirm
        // and auto-confirm paths because the DB trigger has already
        // written profile + onboarding_completed=true via user_metadata.
        // Idempotent + fail-OPEN on the server side. (2026-05-14)
        if (newUserId) {
          fireSignupNotification(newUserId);
        }
        if (!session) {
          // Email confirmation required (prod default). Surface interstitial.
          // The trigger has already written the profile + onboarding_completed
          // = true, so on email-link return the user lands at /pending-approval
          // (or /cockpit if pre-approved) — NOT back through the wizard.
          setSignupLoading(false);
          setPendingEmail(credentials.email);
          return;
        }
        // Auto-confirmed (local dev) — profile already written by the
        // trigger. Just refresh the cache + navigate. We patch the same
        // values defensively so usePatchUserProfile's onSuccess hydrates
        // the query cache, eliminating any flash of stale `needsOnboarding`
        // on the /cockpit landing's first paint.
        try {
          await patchProfile.mutateAsync({
            firm_name: values.firm_name,
            // PATCH wire shape uses `work_role` (renamed 2026-05-25).
            work_role: values.work_role,
            asset_classes: values.asset_classes,
            markets: values.markets,
            focus: values.focus,
            volume: values.volume,
            onboarding_completed: true,
            terms_accepted_at: termsAcceptedAt,
          });
          if (shareBridgeHref) {
            // Route through the share-link bridge so linkSharesForUser fires.
            window.location.assign(shareBridgeHref);
          } else {
            navigate(
              nextTarget
                ? ({ to: nextTarget } as { to: string })
                : { to: '/cockpit' }
            );
          }
        } catch (err) {
          setSignupError(
            err instanceof Error ? err.message : 'Profile save failed'
          );
        } finally {
          setSignupLoading(false);
        }
      }}
      onLoginClick={() => navigate({ to: '/login' })}
    />
  );
}
